import React, { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";

import _ from "lodash";
import cx from "classnames";

import styles from "./view.module.scss";
import Button from "src/components/button";
import dateFormat from "dateformat";
import moment from "moment";
import { BannerView } from "src/components/banner";
import { LearningRecordSimulation } from "src/types/coaching/learners";
import { SimulationContext } from "./context";
import { useTransition, animated } from "@react-spring/web";
import { CallSimulator } from "./simulator";
import { VapiContextProvider } from "./simulator/hook";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";
import Text from "src/components/text";
import Thumbnail from "src/components/thumbnail";
import { getDueDate } from "../../course/view";
import { useSelector } from "react-redux";

interface SimulationSummaryComponentProps {
  simulation: LearningRecordSimulation;
  className?: string;
  isReadOnly?: boolean;
}

const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'German', value: 'de' },
  { label: 'French', value: 'fr' },
  { label: 'Marathi', value: 'mr' },
];

export const SimulationSummaryComponent: React.FC<SimulationSummaryComponentProps> = ({ simulation , className, isReadOnly}) => {
  const simulationContext = React.useContext(SimulationContext);

  const summaryView = simulationContext.view === "summary";
  const session = useMemo(() => _.get(simulation, "session"), [simulation]);

  const viewTransition = useTransition(summaryView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const callTransition = useTransition(!summaryView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleStartSimulation = () => {
    if (simulationContext.startSimulation) {
      simulationContext.startSimulation();
    }
  }

  const handleViewReport = () => {
    if (simulationContext.viewReport) {
      simulationContext.viewReport();
    }
  }

  useEffect(() => {
    if (session) {
      if (simulationContext.changeView) {
        simulationContext.changeView("simulator");
      }
    }
  }, [session]);

  return (
    <div className={styles.simulationContainer}>
      {viewTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <SimulationView simulation={simulation} onStart={handleStartSimulation} onViewReport={handleViewReport} className={className} isReadOnly={isReadOnly} />
            </animated.div>
          ),
      )}

      {callTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <VapiContextProvider>
                <CallSimulator simulation={simulation} className={className} />
              </VapiContextProvider>
            </animated.div>
          ),
      )}
    </div>
  );
};

interface SimulationViewProps {
  simulation: LearningRecordSimulation;
  onStart: () => void;
  onViewReport: () => void;
  className?: string;
  isReadOnly?: boolean;
}

const SimulationView: React.FC<SimulationViewProps> = ({ simulation, onStart, onViewReport, className, isReadOnly }) => {

  const simulationContext = React.useContext(SimulationContext);
  

  const name = useMemo(() => _.get(simulation, "simulation.name") || "", [simulation]);
  const status = useMemo(() => _.get(simulation, "status"), [simulation]);
  const description = useMemo(() => _.get(simulation, "simulation.description") || "", [simulation]);
  const banner = useMemo(() => _.get(simulation, "simulation.banner"), [simulation]);
  const type = useMemo(() => _.get(simulation, "simulation.type",null), [simulation])
  const createdBy = useMemo(() => _.get(simulation, "simulation.publishedBy.icon",null), [simulation])
  const firstName = useMemo(() => _.get(simulation, "simulation.publishedBy.firstName", null), [simulation])
  const lastName = useMemo(() => _.get(simulation, "simulation.publishedBy.lastName", null), [simulation])
  const dueDate = useMemo(()=> _.get(simulation, "dueDate",null), [simulation])

  const callType = useMemo(() => {
    const value = _.get(simulation, "simulation.type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(simulation, "simulation.difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [simulation]);

  const language = useMemo(() => {
    const languageValue = _.get(simulation, "simulation.language");
  
    const option = languageOptions.find(option => option.value === languageValue);
  
    return option?.label || "English";
  }, [simulation]);

  const isOverdue = dueDate && moment(dueDate).isBefore(moment(), "day");

  const handleBackClicked = () => {
    if (simulationContext.onBack) {
      simulationContext.onBack();
    }
  }

  const handleEnrollSimulation = () => {
    if (simulationContext.enrollSimulation) {
      simulationContext.enrollSimulation();
    }
  }

  const handleStartSimulation = () => {
    onStart();
  }

  const handleRestartSimulation = () => {
    simulationContext.restartSimulation?.();
  }

  const handleViewReports = () => {
    onViewReport();
  }

  const handleRetakeSimulation = () => {
    if (simulationContext.retakeSimulation) {
      simulationContext.retakeSimulation();
    }
  }

  return (
    <div className={cx(styles.simulationViewContainer, className)}>
      <div className={styles.left}>
        <div className={styles.simulationDetails}>
          {simulationContext.onBack && (
            <div className={styles.backButton} onClick={handleBackClicked}>
              <i className="paperflite-left-arrow"></i>
              <div>
                <span className={styles.back}>back</span>{" "}
              </div>
            </div>
          )}

          <div className={styles.details}>
            <div className={styles.header}>
              <div
                className={cx(
                  styles.thumbnail,
                  (type === "discovery" && styles.discovery) ||
                    (type === "cold_call" && styles.coldcall),
                )}
              >
                <BannerView
                  banner={banner as any}
                  defaultBanner="/images/assets/simulationThumbnail.jpg"
                  overlay={false}
                />
              </div>
              <div className={styles.right}>
                <Text className={styles.name} text={name} lines={2} />
                <div className={styles.actions}>
                  {status === "NOT_ENROLLED" && (
                    <Button
                      className={styles.startCourse}
                      text={
                        simulationContext.enrollingSimulation
                          ? "Enrolling..."
                          : "Enroll Call"
                      }
                      variant="courses-primary"
                      icon="paperflite-phone"
                      disabled={simulationContext.enrollingSimulation}
                      onClick={handleEnrollSimulation}
                    />
                  )}

                  {status === "NOT_STARTED" && (
                    <Button
                      className={styles.startCourse}
                      text={
                        simulationContext.startingSimulation
                          ? "Starting..."
                          : `Start Call ${getDueDate(dueDate!)}`
                      }
                      variant="courses-primary"
                      icon="paperflite-phone"
                      disabled={simulationContext.startingSimulation}
                      onClick={handleStartSimulation}
                    />
                  )}

                  {status === "COMPLETED" &&
                    simulation?.report?.status !== "in_sufficient_duration" && (
                      <>
                        {simulation.report?.status === "completed" && (
                          <Button
                            className={styles.viewReports}
                            text={
                              simulationContext.fetchingReport
                                ? "Fetching report..."
                                : "View Reports"
                            }
                            variant="courses-primary"
                            icon="paperflite-analytics"
                            disabled={
                              simulationContext.fetchingReport ||
                              simulationContext.retakingSimulation
                            }
                            onClick={handleViewReports}
                          />
                        )}

                        {simulation.report?.status !== "completed" && (
                          <Button
                            className={styles.proceesingReports}
                            text={"Processing call..."}
                            variant="courses-primary"
                            icon="paperflite-analytics"
                          />
                        )}
                      </>
                    )}

                  {status === "COMPLETED" &&
                    simulation?.report?.status == "in_sufficient_duration" && (
                      <Button
                        className={styles.viewReports}
                        text={
                          simulationContext.restartingSimulation
                            ? "Restarting..."
                            : "Restart Simulation"
                        }
                        variant="courses-primary"
                        icon="paperflite-reverse"
                        disabled={simulationContext.restartingSimulation}
                        onClick={handleRestartSimulation}
                      />
                    )}

                  {status === "COMPLETED" &&
                    simulation?.report?.status !== "in_sufficient_duration" && (
                      <Button
                        className={styles.startCourse}
                        text={
                          simulationContext.retakingSimulation
                            ? "Retaking..."
                            : "Retake Simulation"
                        }
                        variant="courses-primary"
                        icon="paperflite-reverse"
                        disabled={simulationContext.retakingSimulation}
                        onClick={handleRetakeSimulation}
                      />
                    )}
                </div>
              </div>
            </div>

            <div className={styles.simulationMetadata}>
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {`${simulation.simulation.duration} Minutes`}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-calendar" />
                {callType}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-speedomenter" />
                {difficulty}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-language" />
                {language}
              </div>
              {!isReadOnly && (
                 <div className={cx(styles.metadata,styles.profile)}>
                 <Thumbnail
                   media={createdBy}
                   defaultImage="/images/assets/default_asset.jpg"
                   className={styles.icon}
                 />
                 by {firstName} {lastName}
               </div>
              )}
            </div>

            <div className={styles.description}>
              <p className={styles.title}>Description</p>
              <div
                className={styles.descriptionContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {status === "COMPLETED" && (simulation?.score ?? 0) > 0 && (
          <div className={styles.scoreContainer}>
            <h6 className={styles.name}>Your Score</h6>
            <div className={styles.details}>
              <div className={styles.label}>
                here&apos;s your score for this simulation
              </div>
              <div className={styles.score}>
                {Math.min(
                  Number(((simulation.score || 0) * 100).toFixed(2)),
                  100,
                )}
                %
              </div>
            </div>
          </div>
        )}
        <div className={styles.property}>
          <h6 className={styles.name}>Organization Info</h6>
          { simulation.simulation?.organization && simulation.simulation?.organization?.description &&
          <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.name}>
                {simulation.simulation?.organization?.name || ""}
              </div>
              <div className={styles.description}>
                {simulation.simulation?.organization?.description || ""}
              </div>
            </div>
          </div>
          }
          { simulation.simulation?.organization && !simulation.simulation?.organization.description &&
            <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.description}>
                No information
              </div>
            </div>
          </div>
          }
        </div>
        <div className={styles.property}>
          <h6 className={styles.name}>Persona</h6>
          <div className={styles.body}>
            <div className={styles.persona}>
              <div className={styles.name}>
                {simulation.simulation?.persona?.name || ""}
              </div>
              <div className={styles.description}>
                {simulation.simulation?.persona?.description || ""}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.property}>
          <h6 className={styles.name}>Objectives</h6>
          <div className={styles.body}>
            <div className={styles.objectives}>
              <div className={styles.description}>
                {simulation.simulation?.objectives?.description || ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}